const normalizeId = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  if (
    typeof value !== 'string' &&
    typeof value !== 'number' &&
    typeof value !== 'bigint'
  ) {
    return undefined;
  }

  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : undefined;
};

/** The spellings a row's creator / maker user id arrives under. */
const MAKER_ID_FIELDS = [
  'createdByUserId',
  'CreatedByUserId',
  'created_by_user_id',
  'createdByUserID',
  'makerId',
  'MakerId',
];

export const getCreatedByUserId = (row: Record<string, any> | undefined) => {
  if (!row) return undefined;

  // Prioritize explicit ID fields to avoid picking up name strings from 'updatedBy'
  return normalizeId(
    [row.lastMakerId, ...MAKER_ID_FIELDS.map(field => row[field])].find(
      id => id !== null && id !== undefined
    )
  );
};

export const isSelfCreatedMaintenanceRecord = (
  row: Record<string, any> | undefined,
  currentUserId: string | number | undefined
): boolean => {
  const creatorId = getCreatedByUserId(row);
  const userId = normalizeId(currentUserId);

  if (!creatorId || !userId) return false;
  return creatorId === userId;
};

/**
 * True when the current user is either maker of record — the creator or the
 * last maker. Unlike `isSelfCreatedMaintenanceRecord`, which compares a
 * single resolved id, this checks both: a case one maker started and another
 * carried on bars both from acting on it as checker.
 */
export const isMakerOfRecord = (
  row: Record<string, any> | null | undefined,
  currentUserId: string | number | null | undefined
): boolean => {
  const userId = normalizeId(currentUserId);
  if (!row || !userId) return false;

  // Every spelling, not just the first present: the creator and the last
  // maker are both barred, so neither may shadow the other.
  return [row.lastMakerId, ...MAKER_ID_FIELDS.map(field => row[field])].some(
    makerId => normalizeId(makerId) === userId
  );
};

/** The workflow status of a request waiting on the checker. */
export const PENDING_CHECKER_STATUS = 'Pending Checker Approval';

/** True when a workflow request (grid row) asks to delete its record. */
export const isDeleteRequest = (
  row: { workflowActionName?: string | null } | null | undefined
): boolean => row?.workflowActionName?.toLowerCase() === 'delete';

/**
 * The permission a maker needs to reopen their own Maker In Progress / Pending
 * Revision request on its approve-flow page: `<Module>.Edit` to revise it, or
 * `<Module>.Delete` for a deletion request, which has nothing to edit.
 */
export const makerRequestPermission = (
  permissionPrefix: string,
  isDelete: boolean
): string => `${permissionPrefix}.${isDelete ? 'Delete' : 'Edit'}`;

/** `makerRequestPermission` for a caller holding capability flags. */
export const canOpenMakerRequest = (
  isDelete: boolean,
  capabilities: { canEdit?: boolean; canDelete?: boolean }
): boolean => Boolean(isDelete ? capabilities.canDelete : capabilities.canEdit);
